import React, { ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { makeStyles } from '@material-ui/styles'
import { Tooltip } from '@material-ui/core'
import HelpOutline from '@material-ui/icons/HelpOutline'
import { Row, Col, Typography, TextLabel } from '@copyrightagent/basic-components'
import CaseState from 'components/CaseStatus'
import { ICaseState, getFormatOccurredDate } from 'lib/entities/Case'
import {
	ISubmittedCase,
	PARTNER_STATE_LABEL,
	getRejectionDetail,
} from 'lib/entities/submittedCaseStatus'
import { RouteUrls } from 'routes/RouteUrls'
import { blue, dark50 } from 'lib/Colors'
import { resubmitCase } from 'routes/Private/Cases/api'
import { useStyle } from '../CaseRow/style'

interface Props {
	theCase: ISubmittedCase
}

const useLinkStyle = makeStyles(
	() => ({
		caseLink: {
			marginLeft: 12,
			cursor: 'pointer',
		},
		// Mirrors basic-components' InfoIconWithTooltip — same dark50 icon, same
		// help cursor, same arrow and ~300px cap — but with a question mark rather
		// than its fixed info glyph.
		helpIcon: {
			marginLeft: 8,
			color: dark50,
			cursor: 'help',
			outline: 'none',
		},
		helpTooltip: {
			maxWidth: 320,
			fontSize: 13,
			lineHeight: 1.5,
			padding: '8px 12px',
		},
		// Sits on the status line rather than under it, so a rejected row is the
		// same height whether or not it can be resubmitted.
		resubmit: {
			marginLeft: 12,
			padding: 0,
			border: 'none',
			background: 'none',
			fontFamily: 'inherit',
			fontSize: 13,
			whiteSpace: 'nowrap',
			color: blue,
			cursor: 'pointer',
			'&:hover': {
				textDecoration: 'underline',
			},
			'&:disabled': {
				color: dark50,
				cursor: 'default',
				textDecoration: 'none',
			},
		},
		resubmitOutcome: {
			marginLeft: 12,
			fontSize: 13,
			whiteSpace: 'nowrap',
			color: dark50,
			cursor: 'help',
		},
	}),
	{ index: 1 },
)

/**
 * Offered only where the API says republishing would actually reopen the case
 * (`can_resubmit`). A case the pipeline would drop again — one that already
 * became a client case, which includes every case a partner saw and dismissed —
 * gets no button, because clicking it would silently change nothing.
 */
function ResubmitAction({ theCase }: Props): ReactElement {
	const { t } = useTranslation()
	const classes = useLinkStyle()
	const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'failed'>(
		'idle',
	)

	const send = async () => {
		if (!theCase.submitted_case_id) return
		setState('sending')
		const response = await resubmitCase(theCase.submitted_case_id)
		// The API re-checks reopen-ability, so a case that became a client case
		// since this list loaded is refused here rather than dropped in silence.
		setState(response.isSuccessful ? 'sent' : 'failed')
	}

	// Both outcomes are a short label carrying the explanation in a tooltip: the
	// full sentences are what a partner needs once, not on every row of the list.
	// role="status" so the outcome is announced — the button unmounts on success,
	// which would otherwise drop focus with no sign the click did anything.
	if (state === 'sent' || state === 'failed') {
		const sent = state === 'sent'
		const detail = String(
			t(sent ? 'createCase/resubmitted' : 'createCase/resubmitFailed'),
		)
		return (
			<Tooltip
				arrow
				placement="bottom-start"
				classes={{ tooltip: classes.helpTooltip }}
				title={detail}
			>
				<span
					role="status"
					tabIndex={0}
					className={classes.resubmitOutcome}
					aria-label={detail}
				>
					{t(
						sent
							? 'createCase/resubmittedShort'
							: 'createCase/resubmitFailedShort',
					)}
				</span>
			</Tooltip>
		)
	}

	return (
		<Tooltip
			arrow
			placement="bottom-start"
			classes={{ tooltip: classes.helpTooltip }}
			title={String(t('createCase/resubmitHint'))}
		>
			<button
				type="button"
				className={classes.resubmit}
				disabled={state === 'sending'}
				onClick={send}
			>
				{t(state === 'sending' ? 'createCase/resubmitting' : 'createCase/resubmit')}
			</button>
		</Tooltip>
	)
}

// Renders the derived outcome of a submitted case:
// - dropped by a filter   -> red "Not accepted" + the specific reason
// - rejected case         -> red "Rejected" + who rejected it and why
// - partner-facing state  -> CaseStatus icon + label
// - internal/unknown/none -> "Processing"
// Plus a link to the existing case whenever the violation has a case_id.
export default function CreatedCaseStatus({ theCase }: Props): ReactElement {
	const { t } = useTranslation()
	const history = useHistory()
	const classes = useStyle()
	const linkClasses = useLinkStyle()

	// On hold overrides the state label rather than replacing the state: a parked
	// case keeps its real state (normally client_approval), so without this it
	// would read "Waiting for review". Not gated on client_approval — putOnHold
	// does not restrict which state a case can be parked in.
	const stateLabelKey = theCase.on_hold
		? 'caseState/on_hold'
		: theCase.state && PARTNER_STATE_LABEL[theCase.state as ICaseState]

	let statusNode: ReactElement
	if (theCase.rejected_reason) {
		statusNode = (
			<Col>
				<TextLabel
					uppercase
					color="red"
					labelText={t('createCase/notAccepted')}
				/>
				<Row align="center">
					<Typography fontColor={dark50}>
						{t(`createCase/rejectedReason/${theCase.rejected_reason}`)}
					</Typography>
					{theCase.can_resubmit ? <ResubmitAction theCase={theCase} /> : <></>}
				</Row>
			</Col>
		)
	} else if (theCase.violation_id && theCase.state === 'rejected') {
		// A rejection is never shown as a bare label: getRejectionDetail always
		// resolves to a sentence naming who decided, and why where we know it.
		// That sentence is a paragraph, so it hangs off a "?" rather than setting
		// the height of every row in the list.
		const { labelKey, reasonKey } = getRejectionDetail(theCase)
		const explanation = t(labelKey, {
			reason: reasonKey ? t(reasonKey) : '',
			date: theCase.rejection?.occurred_at
				? getFormatOccurredDate(theCase.rejection.occurred_at)
				: '',
		})
		statusNode = (
			<Row align="center">
				<TextLabel uppercase color="red" labelText={t('createCase/rejected')} />
				<Tooltip
					arrow
					title={explanation}
					placement="bottom-start"
					classes={{ tooltip: linkClasses.helpTooltip }}
				>
					{/* tabIndex so the explanation is reachable without a pointer. */}
					<HelpOutline
						fontSize="small"
						tabIndex={0}
						aria-label={explanation}
						className={linkClasses.helpIcon}
					/>
				</Tooltip>
				{theCase.can_resubmit ? <ResubmitAction theCase={theCase} /> : <></>}
			</Row>
		)
	} else if (theCase.violation_id && stateLabelKey) {
		statusNode = (
			<Row>
				<CaseState theCase={theCase as never} className={classes.chip} />
				<Typography fontWeight={600} className={classes.caseStatusText}>
					{t(stateLabelKey)}
				</Typography>
			</Row>
		)
	} else {
		statusNode = (
			<TextLabel
				uppercase
				color="blue"
				labelText={t('createCase/processing')}
			/>
		)
	}

	return (
		<Row justify="flex-start" align="center">
			{statusNode}
			{theCase.case_id ? (
				<TextLabel
					uppercase
					color="blue"
					labelText={`${theCase.case_id} >`}
					className={linkClasses.caseLink}
					onClick={() => history.push(`${RouteUrls.case}/${theCase.case_id}`)}
				/>
			) : (
				<></>
			)}
		</Row>
	)
}
